import { c as _c } from "react/compiler-runtime";
import { IcoEyeCrossed16 } from '@onefootprint/icons';
import type { ErrorDetail } from '@onefootprint/request-types/dashboard';
import { InlineAlert, Tooltip } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import useDirty from '../../hooks/use-dirty';
import useSidebar from '../../hooks/use-sidebar';
import type { ApplyEditMutation } from '../base-node';
import type { Banner } from '../error-warning-list/error-warning-list';
type CustomErrorComponentProps = {
  mutation: ApplyEditMutation;
  type: Banner['type'];
  error: ErrorDetail;
};
export type CustomErrorComponent = (props: CustomErrorComponentProps) => React.ReactNode;
export const ERRORS_WARNINGS_DISPLAY_ID = 'sidebar-errors-warnings-display';
const MULTI_NODE_ERROR_CODE = 'E136';
const TEXT_COLOR_BY_TYPE: Record<Banner['type'], string> = {
  error: 'text-error',
  warning: 'text-warning',
  info: 'text-secondary'
};
const ALERT_VARIANT_BY_TYPE: Record<Banner['type'], 'error' | 'warning' | 'neutral'> = {
  error: 'error',
  warning: 'warning',
  info: 'neutral'
};
type ErrorsWarningsDisplayProps = {
  errors: Banner[];
  mutation: ApplyEditMutation;
  customErrorComponents?: Record<string, CustomErrorComponent>;
};
const ErrorsWarningsDisplay = t0 => {
  const $ = _c(13);
  const {
    errors,
    mutation,
    customErrorComponents
  } = t0;
  const {
    isAnyComponentDirty
  } = useDirty();
  const {
    open: openNode
  } = useSidebar();
  const {
    t
  } = useTranslation("playbook-builder");
  let t1;
  if ($[0] !== customErrorComponents || $[1] !== isAnyComponentDirty || $[2] !== mutation || $[3] !== openNode || $[4] !== t) {
    t1 = (error, type) => {
      const customComponent = error.code && customErrorComponents?.[error.code];
      if (customComponent) {
        return customComponent({
          error,
          mutation,
          type
        });
      }
      const otherNodeId = error.code === MULTI_NODE_ERROR_CODE ? error.context?.otherNodeId as string : undefined;
      const otherNodeLink = otherNodeId ? <button type="button" onClick={() => openNode(otherNodeId)} className={cx(["ml-1 underline", TEXT_COLOR_BY_TYPE[type], {
        "cursor-pointer hover:opacity-70 active:opacity-85": !isAnyComponentDirty,
        "opacity-50 cursor-not-allowed": isAnyComponentDirty
      }])} disabled={isAnyComponentDirty}>{t(_temp)}</button> : null;
      const isInternalOnly = error.context?.internalOnly === true;
      return <div className="flex items-start gap-1.5">{isInternalOnly && <Tooltip content={t(_temp2)} side="left"><div className="mt-2 flex-shrink-0 text-tertiary"><IcoEyeCrossed16 /></div></Tooltip>}<InlineAlert variant={ALERT_VARIANT_BY_TYPE[type]}><span className={cx("text-body-2", TEXT_COLOR_BY_TYPE[type])}>{error.message}{otherNodeLink}</span></InlineAlert></div>;
    };
    $[0] = customErrorComponents;
    $[1] = isAnyComponentDirty;
    $[2] = mutation;
    $[3] = openNode;
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const renderError = t1;
  let t2;
  if ($[6] !== errors || $[7] !== renderError) {
    let t3;
    if ($[9] !== renderError) {
      t3 = item => <div key={item.description.message}>{renderError(item.description, item.type)}</div>;
      $[9] = renderError;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t2 = errors.map(t3);
    $[6] = errors;
    $[7] = renderError;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[11] !== t2) {
    t3 = <div className="flex flex-col gap-1 px-4 [&>*:first-child]:mt-4" id={ERRORS_WARNINGS_DISPLAY_ID}>{t2}</div>;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
export default ErrorsWarningsDisplay;
function _temp($) {
  return $.errorsWarningsDisplay.viewOtherNode;
}
function _temp2($_0) {
  return $_0.errorsWarningsDisplay.internalOnly;
}